import { useState, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Linking,
  Platform,
  ActivityIndicator,
  Image,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useRouter, useLocalSearchParams } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import type { Event } from "../../lib/types";

const API_URL = (process.env.EXPO_PUBLIC_API_URL || "https://dashhound.app") + "/api";

function DetailRow({ icon, iconColor, label, value, onPress }: {
  icon: string;
  iconColor?: string;
  label: string;
  value: string;
  onPress?: () => void;
}) {
  const Wrapper = onPress ? TouchableOpacity : View;
  return (
    <Wrapper onPress={onPress} style={{ flexDirection: "row", padding: 16, borderBottomWidth: 1, borderBottomColor: "#f3f4f6", alignItems: "flex-start" }}>
      <Ionicons name={icon as any} size={20} color={iconColor || "#1B2A4A"} style={{ marginRight: 12, marginTop: 2 }} />
      {/* minWidth: 0 lets the flex child shrink correctly on RN Web so long
          unbroken strings wrap instead of overflowing the row. */}
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ fontSize: 14, fontWeight: "600", color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</Text>
        <Text style={{ fontSize: 14, color: "#111827", marginTop: 3, flexShrink: 1 }}>{value}</Text>
        {onPress && <Text style={{ fontSize: 14, color: "#1B2A4A", marginTop: 4 }}>Tap to open →</Text>}
      </View>
    </Wrapper>
  );
}

export default function EventDetailScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (id) fetchEvent();
  }, [id]);

  async function fetchEvent() {
    try {
      const res = await fetch(`${API_URL}/events/${id}`, {
        headers: { "Accept": "application/json" },
      });
      if (!res.ok) throw new Error("Event not found");
      setEvent(await res.json());
    } catch (err: any) {
      setError(err.message || "Failed to load event");
    } finally {
      setLoading(false);
    }
  }

  function formatDate(dateStr: string | null): string {
    if (!dateStr) return "";
    try {
      // Force UTC interpretation — dates are stored as UTC midnight, and rendering
      // in local TZ shifts them backwards (June 5 UTC midnight reads as June 4 in ET).
      return new Date(dateStr).toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      });
    } catch { return dateStr; }
  }

  function openMaps() {
    if (!event) return;
    const fullAddress = [event.address, event.city, event.state, event.zip].filter(Boolean).join(", ");
    const query = fullAddress ? encodeURIComponent(fullAddress) : `${event.latitude},${event.longitude}`;
    const url = `https://www.google.com/maps/search/?api=1&query=${query}`;
    if (Platform.OS === "web") window.open(url, "_blank");
    else Linking.openURL(url);
  }

  function openUrl(url: string) {
    if (Platform.OS === "web") window.open(url, "_blank");
    else Linking.openURL(url);
  }

  if (loading) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8", justifyContent: "center", alignItems: "center" }}>
        <ActivityIndicator size="large" color="#1B2A4A" />
      </SafeAreaView>
    );
  }

  if (error || !event) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8" }}>
        <View style={{ flexDirection: "row", alignItems: "center", paddingHorizontal: 16, paddingVertical: 14, backgroundColor: "#fff", borderBottomWidth: 1, borderBottomColor: "#e5e7eb" }}>
          <TouchableOpacity onPress={() => router.back()} style={{ marginRight: 12 }}>
            <Ionicons name="arrow-back" size={22} color="#1B2A4A" />
          </TouchableOpacity>
          <Text style={{ fontSize: 18, fontWeight: "700", color: "#111827" }}>Event Details</Text>
        </View>
        <View style={{ padding: 20, alignItems: "center" }}>
          <Text style={{ color: "#dc2626" }}>{error || "Event not found"}</Text>
        </View>
      </SafeAreaView>
    );
  }

  const fullAddress = [event.address, event.city, event.state, event.zip].filter(Boolean).join(", ");
  const isClosingDatePassed = event.closingDate && new Date(event.closingDate) < new Date();
  const isEventPassed = event.date && new Date(event.date) < new Date();
  const isOpen = event.openingDate && new Date(event.openingDate) <= new Date() && !isClosingDatePassed;

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8" }}>
      {/* Header */}
      <View style={{ flexDirection: "row", alignItems: "center", paddingHorizontal: 16, paddingVertical: 14, backgroundColor: "#fff", borderBottomWidth: 1, borderBottomColor: "#e5e7eb" }}>
        <TouchableOpacity onPress={() => router.canGoBack() ? router.back() : router.replace("/(tabs)/events" as any)} style={{ marginRight: 12 }}>
          <Ionicons name="arrow-back" size={22} color="#1B2A4A" />
        </TouchableOpacity>
        <Text style={{ fontSize: 18, fontWeight: "700", color: "#111827", flex: 1 }} numberOfLines={1}>Event Details</Text>
      </View>

      <ScrollView contentContainerStyle={{ padding: 20, paddingBottom: 60 }}>
        {/* Event header card */}
        <View style={{ backgroundColor: "#1B2A4A", borderRadius: 16, padding: 20, marginBottom: 20, position: "relative" }}>
          {event.clubLogoUrl && (() => {
            const isWide = event.clubLogoStyle === "wide";
            const containerWidth = isWide ? 130 : 82;
            const containerHeight = isWide ? 76 : 82;
            const radius = isWide ? 12 : 41;
            const innerRadius = isWide ? 10 : 39;
            return (
              <View style={{
                position: "absolute",
                top: 14,
                right: 14,
                width: containerWidth,
                height: containerHeight,
                borderRadius: radius,
                backgroundColor: "#fff",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                borderWidth: isWide ? 1 : 0,
                borderColor: "#e5e7eb",
                padding: isWide ? 4 : 0,
              }}>
                <Image
                  source={{ uri: event.clubLogoUrl }}
                  style={{ width: isWide ? containerWidth - 12 : 78, height: isWide ? containerHeight - 12 : 78, borderRadius: innerRadius }}
                  resizeMode="contain"
                />
              </View>
            );
          })()}
          <Text style={{ fontSize: 22, fontWeight: "800", color: "#fff", paddingRight: event.clubLogoUrl ? (event.clubLogoStyle === "wide" ? 142 : 94) : 0 }}>{event.name}</Text>
          {event.club && event.club !== event.name && (
            <Text style={{ fontSize: 14, color: "#94A3B8", marginTop: 4, paddingRight: event.clubLogoUrl ? (event.clubLogoStyle === "wide" ? 142 : 94) : 0 }}>Hosted by {event.club}</Text>
          )}
          <View style={{ flexDirection: "row", alignItems: "center", marginTop: 10, gap: 6 }}>
            <Ionicons name="calendar" size={16} color="#93c5fd" />
            <Text style={{ fontSize: 15, color: "#fff", fontWeight: "600" }}>{formatDate(event.date)}</Text>
          </View>
          {/* Status badges */}
          <View style={{ flexDirection: "row", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            {event.eventSetting && (
              <View style={{ backgroundColor: "rgba(255,255,255,0.2)", borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3 }}>
                <Text style={{ color: "#fff", fontSize: 14, fontWeight: "600" }}>{event.eventSetting}</Text>
              </View>
            )}
            {isOpen && (
              <View style={{ backgroundColor: "#22c55e", borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3 }}>
                <Text style={{ color: "#fff", fontSize: 14, fontWeight: "700" }}>Entries Open</Text>
              </View>
            )}
            {isClosingDatePassed && !isEventPassed && (
              <View style={{ backgroundColor: "#ef4444", borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3 }}>
                <Text style={{ color: "#fff", fontSize: 14, fontWeight: "600" }}>Entries Closed</Text>
              </View>
            )}
            {isEventPassed && (
              <View style={{ backgroundColor: "rgba(255,255,255,0.2)", borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3 }}>
                <Text style={{ color: "#fff", fontSize: 14, fontWeight: "600" }}>Past Event</Text>
              </View>
            )}
            {event.entryFee && (
              <View style={{ backgroundColor: "#fbbf24", borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3 }}>
                <Text style={{ color: "#78350f", fontSize: 14, fontWeight: "700" }}>{event.entryFee}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Sign Up button */}
        {event.entryUrl && !isEventPassed && (
          <TouchableOpacity
            onPress={() => openUrl(event.entryUrl!)}
            style={{
              backgroundColor: "#F5A623",
              borderRadius: 12,
              padding: 16,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 20,
              gap: 8,
            }}
          >
            <Ionicons name="create-outline" size={22} color="#ffffff" />
            <Text style={{ color: "#ffffff", fontSize: 18, fontWeight: "800" }}>Sign Up for This Event</Text>
          </TouchableOpacity>
        )}

        {/* Entry Info */}
        <Text style={{ fontSize: 16, fontWeight: "700", color: "#1B2A4A", marginBottom: 10 }}>Entry Information</Text>
        <View style={{ backgroundColor: "#fff", borderRadius: 14, borderWidth: 1, borderColor: "#e5e7eb", overflow: "hidden", marginBottom: 20 }}>
          {event.entryFee && (
            <DetailRow icon="cash-outline" label="Entry Fee" value={event.entryFee} />
          )}
          {event.dayOfRunsFee && (
            <DetailRow icon="alarm-outline" label="Day of Runs" value={event.dayOfRunsFee} />
          )}
          {event.openingDate && (
            <DetailRow
              icon="lock-open-outline"
              iconColor="#22c55e"
              label="Opens"
              value={`${formatDate(event.openingDate)}${event.openingTime ? ` at ${event.openingTime}` : ""}${event.timeZone ? ` ${event.timeZone}` : ""}`}
            />
          )}
          {event.closingDate && (
            <DetailRow
              icon="lock-closed-outline"
              iconColor={isClosingDatePassed ? "#dc2626" : "#f59e0b"}
              label="Closes"
              value={`${formatDate(event.closingDate)}${event.closingTime ? ` at ${event.closingTime}` : ""}${event.timeZone ? ` ${event.timeZone}` : ""}${isClosingDatePassed ? " (Closed)" : ""}`}
            />
          )}
          {event.eligibleBreeds && (
            <DetailRow icon="paw-outline" label="Eligible Breeds" value={event.eligibleBreeds} />
          )}
          {event.premiumListUrl && (
            <DetailRow
              icon="document-text-outline"
              label="Premium List"
              value="Download entry info & details"
              onPress={() => openUrl(event.premiumListUrl!)}
            />
          )}
        </View>

        {/* Location */}
        <Text style={{ fontSize: 16, fontWeight: "700", color: "#1B2A4A", marginBottom: 10 }}>Location</Text>
        <View style={{ backgroundColor: "#fff", borderRadius: 14, borderWidth: 1, borderColor: "#e5e7eb", overflow: "hidden", marginBottom: 20 }}>
          {fullAddress ? (
            <DetailRow
              icon="location"
              label="Venue"
              value={fullAddress}
              onPress={openMaps}
            />
          ) : null}
        </View>

        {/* Contact Info */}
        {(event.eventChair || event.eventChairContact || event.secretary || event.secretaryContact || event.clubWebsite || event.lureOperator || event.eventPhotographer || event.eventPhotographerEmail || event.eventPhotographerUrl || (event.eventPhotographers && event.eventPhotographers.length > 0)) && (
          <>
            <Text style={{ fontSize: 16, fontWeight: "700", color: "#1B2A4A", marginBottom: 10 }}>Contact</Text>
            <View style={{ backgroundColor: "#fff", borderRadius: 14, borderWidth: 1, borderColor: "#e5e7eb", overflow: "hidden", marginBottom: 20 }}>
              {(event.eventChair || event.eventChairContact) && (
                <DetailRow
                  icon="person-outline"
                  label="Event Chair"
                  value={[event.eventChair, event.eventChairContact].filter(Boolean).join("\n")}
                />
              )}
              {(event.secretary || event.secretaryContact) && (
                <DetailRow
                  icon="mail-outline"
                  label="Event Secretary"
                  value={[event.secretary, event.secretaryContact].filter(Boolean).join("\n")}
                />
              )}
              {event.lureOperator && (
                <DetailRow
                  icon="flash-outline"
                  label="Lure Operator"
                  value={event.lureOperator}
                />
              )}
              {/* Photographer(s) — render each as its own tappable row */}
              {(() => {
                const photogs = (event.eventPhotographers && event.eventPhotographers.length > 0)
                  ? event.eventPhotographers
                  : (event.eventPhotographer || event.eventPhotographerEmail || event.eventPhotographerUrl)
                    ? [{
                        name: event.eventPhotographer ?? undefined,
                        email: event.eventPhotographerEmail ?? undefined,
                        url: event.eventPhotographerUrl ?? undefined,
                      }]
                    : [];
                if (photogs.length === 0) return null;
                const labelBase = photogs.length > 1 ? "Event Photographer" : "Event Photographer";
                return photogs.map((p, i) => {
                  const label = photogs.length > 1 ? `${labelBase} ${i + 1}` : labelBase;
                  const value = [p.name, p.email, p.url].filter(Boolean).join("\n");
                  const onPress = p.url
                    ? () => openUrl(p.url!.startsWith("http") ? p.url! : `https://${p.url}`)
                    : undefined;
                  return (
                    <DetailRow
                      key={`photog-${i}`}
                      icon="camera-outline"
                      label={label}
                      value={value}
                      onPress={onPress}
                    />
                  );
                });
              })()}
              {event.clubWebsite && (
                <DetailRow
                  icon="globe-outline"
                  label="Club Website"
                  value={event.clubWebsite}
                  onPress={() => openUrl(event.clubWebsite!)}
                />
              )}
            </View>
          </>
        )}

        {/* Get Directions button */}
        {fullAddress && (
          <TouchableOpacity
            onPress={openMaps}
            style={{
              backgroundColor: "#1B2A4A",
              borderRadius: 12,
              padding: 16,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <Ionicons name="navigate" size={20} color="#fff" />
            <Text style={{ color: "#fff", fontSize: 16, fontWeight: "700" }}>Get Directions</Text>
          </TouchableOpacity>
        )}

        {/* What Sets Our Events Apart? — club-supplied highlights */}
        {event.eventWhatSetsApart ? (
          <View style={{ marginTop: 24, backgroundColor: "#FFFBEB", borderWidth: 1, borderColor: "#F5A623", borderRadius: 12, padding: 16 }}>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <Ionicons name="sparkles" size={18} color="#F5A623" />
              <Text style={{ fontSize: 16, fontWeight: "700", color: "#1B2A4A" }}>What Sets Our Events Apart?</Text>
            </View>
            <Text style={{ fontSize: 15, color: "#374151", lineHeight: 22 }}>{event.eventWhatSetsApart}</Text>
          </View>
        ) : null}

        {/* Ribbon Preview(s) — clubs can supply one or many.
            Multi-array (clubRibbonPreviews) wins over the legacy single URL when set. */}
        {(() => {
          const urls = (event.clubRibbonPreviews && event.clubRibbonPreviews.length > 0)
            ? event.clubRibbonPreviews
            : (event.clubRibbonPreviewUrl ? [event.clubRibbonPreviewUrl] : []);
          return urls.map((url, i) => (
            <RibbonPreviewBlock key={`ribbon-${i}`} url={url} />
          ));
        })()}

        {/* Featured gallery — only when club has uploaded photos/videos */}
        {event.clubGallery && (
          ((event.clubGallery.photos?.length ?? 0) > 0 || (event.clubGallery.widePhotos?.length ?? 0) > 0 || (event.clubGallery.videos?.length ?? 0) > 0)
        ) && (
          <View style={{ marginTop: 24 }}>
            <Text style={{ fontSize: 16, fontWeight: "700", color: "#1B2A4A", marginBottom: 12 }}>
              From {event.club || "the club"}
            </Text>

            {/* Videos — full-width player(s) with poster preview */}
            {(event.clubGallery.videos ?? []).map((url, i) => {
              const poster = event.clubGallery?.videoPosters?.[i] ?? null;
              return (
                <View
                  key={`video-${i}`}
                  style={{
                    width: "100%",
                    aspectRatio: 16 / 9,
                    marginBottom: 10,
                    borderRadius: 12,
                    overflow: "hidden",
                    backgroundColor: "#000",
                  }}
                >
                  {Platform.OS === "web" ? (
                    // @ts-ignore — native HTML video element with poster
                    <video
                      src={url}
                      controls
                      playsInline
                      preload="metadata"
                      poster={poster ?? undefined}
                      style={{ width: "100%", height: "100%", objectFit: "cover", backgroundColor: "#000" } as any}
                    />
                  ) : (
                    <TouchableOpacity
                      onPress={() => Linking.openURL(url)}
                      style={{ flex: 1 }}
                      activeOpacity={0.85}
                    >
                      {poster && (
                        <Image
                          source={{ uri: poster }}
                          style={{ position: "absolute", width: "100%", height: "100%" }}
                          resizeMode="cover"
                        />
                      )}
                      <View style={{ flex: 1, alignItems: "center", justifyContent: "center", backgroundColor: poster ? "rgba(0,0,0,0.25)" : "#000" }}>
                        <Ionicons name="play-circle" size={64} color="#fff" />
                      </View>
                    </TouchableOpacity>
                  )}
                </View>
              );
            })}

            {/* Wide photos — full-width, auto-sized to natural aspect ratio. Render before 2-col grid. */}
            {(event.clubGallery.widePhotos ?? []).map((url, i) => (
              <WideGalleryImage key={`wide-${i}`} url={url} />
            ))}

            {/* Photos — 2-up grid (static, not interactive) */}
            {(event.clubGallery.photos ?? []).length > 0 && (
              <>
                <View style={{ flexDirection: "row", flexWrap: "wrap", marginHorizontal: -4 }}>
                  {(event.clubGallery.photos ?? []).map((url, i) => (
                    <View key={`photo-${i}`} style={{ width: "50%", padding: 4 }}>
                      <Image
                        source={{ uri: url }}
                        style={{ width: "100%", aspectRatio: 1, borderRadius: 10, backgroundColor: "#e5e7eb" }}
                        resizeMode="cover"
                      />
                    </View>
                  ))}
                </View>
                {event.clubGallery.photoCredit && (
                  <Text style={{ fontSize: 12, color: "#6b7280", fontStyle: "italic", textAlign: "right", marginTop: 6 }}>
                    {event.clubGallery.photoCredit}
                  </Text>
                )}
              </>
            )}
          </View>
        )}

        {/* Club Raffle — optional, club-supplied prize raffle for the event */}
        {event.clubRaffle && (event.clubRaffle.description || (event.clubRaffle.photos?.length ?? 0) > 0) && (
          <View style={{ marginTop: 24, backgroundColor: "#FEF2F2", borderWidth: 1, borderColor: "#DC2626", borderRadius: 12, padding: 16 }}>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <Ionicons name="gift" size={20} color="#DC2626" />
              <Text style={{ fontSize: 16, fontWeight: "700", color: "#1B2A4A" }}>
                {event.clubRaffle.title || "Club Raffle"}
              </Text>
            </View>

            {event.clubRaffle.description && (
              <Text style={{ fontSize: 15, color: "#374151", lineHeight: 22, marginBottom: 12 }}>
                {event.clubRaffle.description}
              </Text>
            )}

            {event.clubRaffle.drawingDate && (
              <View style={{ flexDirection: "row", alignItems: "center", gap: 6, marginBottom: 12 }}>
                <Ionicons name="calendar" size={16} color="#DC2626" />
                <Text style={{ fontSize: 14, color: "#1B2A4A", fontWeight: "600" }}>
                  Drawing: {event.clubRaffle.drawingDate}
                </Text>
              </View>
            )}

            {(event.clubRaffle.photos?.length ?? 0) > 0 && (
              <View style={{ flexDirection: "row", flexWrap: "wrap", marginHorizontal: -4, marginBottom: 12 }}>
                {(event.clubRaffle.photos ?? []).map((url, i) => (
                  <View key={`raffle-${i}`} style={{ width: "50%", padding: 4 }}>
                    <Image
                      source={{ uri: url }}
                      style={{ width: "100%", aspectRatio: 1, borderRadius: 10, backgroundColor: "#e5e7eb" }}
                      resizeMode="cover"
                    />
                  </View>
                ))}
              </View>
            )}

            {event.clubRaffle.url && (
              <TouchableOpacity
                onPress={() => Linking.openURL(event.clubRaffle!.url!)}
                style={{
                  backgroundColor: "#DC2626",
                  borderRadius: 10,
                  paddingVertical: 12,
                  paddingHorizontal: 16,
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                <Ionicons name="ticket" size={18} color="#fff" />
                <Text style={{ color: "#fff", fontSize: 15, fontWeight: "700" }}>
                  {event.clubRaffle.urlLabel || "Buy Raffle Tickets"}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

// Full-width gallery image that auto-sizes to its natural aspect ratio so any
// shape (square, wide, tall) renders without letterboxing.
function WideGalleryImage({ url }: { url: string }) {
  const [aspect, setAspect] = useState<number | null>(null);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    Image.getSize(
      url,
      (w, h) => { if (!cancelled && w > 0 && h > 0) setAspect(w / h); },
      () => { if (!cancelled) setAspect(4 / 3); }
    );
    return () => { cancelled = true; };
  }, [url]);

  return (
    <Image
      source={{ uri: url }}
      style={{ width: "100%", aspectRatio: aspect ?? 4 / 3, borderRadius: 12, marginBottom: 10, backgroundColor: "#e5e7eb" }}
      resizeMode="cover"
    />
  );
}

// Ribbon preview block that auto-sizes its container to the image's natural aspect
// ratio so there's no letterboxing on either edge — works for any club's image.
function RibbonPreviewBlock({ url }: { url: string }) {
  const [aspect, setAspect] = useState<number | null>(null);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    Image.getSize(
      url,
      (w, h) => { if (!cancelled && w > 0 && h > 0) setAspect(w / h); },
      () => { if (!cancelled) setAspect(4 / 5); } // fallback if size lookup fails
    );
    return () => { cancelled = true; };
  }, [url]);

  return (
    <View style={{ marginTop: 24, backgroundColor: "#fff", borderRadius: 14, borderWidth: 1, borderColor: "#e5e7eb", overflow: "hidden" }}>
      <View style={{ paddingHorizontal: 16, paddingTop: 14, paddingBottom: 10, flexDirection: "row", alignItems: "center", gap: 8 }}>
        <Ionicons name="ribbon" size={20} color="#F5A623" />
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: 16, fontWeight: "800", color: "#1B2A4A" }}>Ribbon Preview</Text>
          <Text style={{ fontSize: 12, color: "#6b7280", marginTop: 1 }}>What you could win at this event</Text>
        </View>
      </View>
      <Image
        source={{ uri: url }}
        style={{ width: "100%", aspectRatio: aspect ?? 4 / 5, backgroundColor: "#fff" }}
        resizeMode="cover"
      />
    </View>
  );
}
